import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useTheme } from '../context/ThemeContext';
import { TourButton } from './OnboardingTour';

export default function Sidebar({ onStartTour }) {
  const { user, logout } = useAuth();
  const { toggle, isDark } = useTheme();
  const navigate = useNavigate();

  async function handleLogout() { await logout(); navigate('/login'); }

  const initials = user?.displayName
    ? user.displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || 'U';

  const statusPageUrl = `${window.location.origin}/status/${user?.uid}`;

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="logo-mark small">IP</div>
        <span className="logo-text">IntegratePulse</span>
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section-label">Monitor</div>

        <NavLink to="/dashboard" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <svg viewBox="0 0 16 16" fill="none">
            <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
            <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity=".4"/>
            <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity=".4"/>
            <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity=".4"/>
          </svg>
          Dashboard
        </NavLink>

        <NavLink to="/integrations/add" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <svg viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M8 5v6M5 8h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          Add integration
        </NavLink>

        <div className="nav-section-label" style={{ marginTop: 8 }}>Configure</div>

        <NavLink to="/alerts/config" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <svg viewBox="0 0 16 16" fill="none">
            <path d="M8 1.5a5 5 0 00-5 5v2.5L1.5 11h13L13 9V6.5a5 5 0 00-5-5z" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M6.5 11.5a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.2"/>
          </svg>
          Alert settings
        </NavLink>

        <NavLink to="/billing" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <svg viewBox="0 0 16 16" fill="none">
            <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M1.5 6.5h13" stroke="currentColor" strokeWidth="1.2"/>
          </svg>
          Billing
        </NavLink>

        <div className="nav-section-label" style={{ marginTop: 8 }}>Share</div>

        <button
          className="nav-item"
          onClick={() => { navigator.clipboard?.writeText(statusPageUrl); alert('Status page URL copied!\n\n' + statusPageUrl); }}
        >
          <svg viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M3.5 8h9M8 3.5c-1.5 2-1.5 7 0 9M8 3.5c1.5 2 1.5 7 0 9" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
          </svg>
          Status page
        </button>
      </nav>

      <div className="sidebar-bottom">
        <button className="theme-toggle" onClick={toggle}>
          {isDark ? (
            <svg viewBox="0 0 16 16" fill="none" width="16" height="16">
              <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M8 1.5v1M8 13.5v1M1.5 8h1M13.5 8h1M3.4 3.4l.7.7M11.9 11.9l.7.7M11.9 3.4l-.7.7M3.4 11.9l-.7.7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" fill="none" width="16" height="16">
              <path d="M13.5 8.5A5.5 5.5 0 017.5 2.5a5.5 5.5 0 100 11 5.5 5.5 0 006-5z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          )}
          {isDark ? 'Light mode' : 'Dark mode'}
        </button>

        <TourButton onStart={onStartTour} />

        <div className="user-row">
          <div className="avatar">{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="user-name">{user?.displayName?.split(' ')[0] || 'User'}</div>
            <div className="user-email">{user?.email}</div>
          </div>
        </div>

        <button className="logout-btn" onClick={handleLogout}>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M8.5 4.5L11 6.5l-2.5 2M11 6.5H5M5 2.5H2.5A1 1 0 001.5 3.5v6a1 1 0 001 1H5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Sign out
        </button>
      </div>
    </aside>
  );
}
